/**
 * The genre tree — the vocabulary is served by `backend-content`; this file
 * draws the MECHANISM and never the values.
 *
 * Native `<details>`/`<summary>` at every level: it works with scripting off,
 * it announces its own state for free, and the active branch ships open. That
 * is the design system's own resolution for this component, not a convenience.
 *
 * The tree ships on this surface deliberately. `[x]` §1.9 states which
 * surfaces are served to a visitor with NO account, which is a floor and not a
 * ceiling — withdrawing the catalogue's densest browse affordance from the
 * people who pay for the catalogue would invert what the subscription buys.
 *
 * **Every destination is a route on THIS host.** It used to leave for
 * `shahedni.com/genres/<slug>` in a new tab, and every row was a 404: the slugs
 * are Arabic prose and the develop edge cannot proxy a non-ASCII path at all —
 * it answers `400 Invalid HTTP request received` before the category page is
 * ever reached. Browsing the catalogue is also the thing a subscriber signed in
 * to do, so the listing is this app's rather than a document to open beside it.
 */
import { Link } from 'react-router-dom';

import { chromeCopy } from './copy';
import { browseHref, genreHref } from './nav';

export interface GenreNode {
  slug: string;
  name: string;
  children?: readonly GenreNode[];
}

export interface GenreTreeProps {
  /** Served by `GET /api/v1/categories` — full depth, ordered by position. */
  nodes?: readonly GenreNode[];
  /** The branch to ship open — the one the current listing sits under. */
  activeSlug?: string;
}

function containsActive(node: GenreNode, activeSlug: string | undefined): boolean {
  if (!activeSlug) return false;
  if (node.slug === activeSlug) return true;
  return (node.children ?? []).some((child) => containsActive(child, activeSlug));
}

function TreeNodes({ nodes, activeSlug }: { nodes: readonly GenreNode[]; activeSlug?: string }) {
  return (
    <ul className="m-0 list-none p-0 text-sm [&_ul]:m-0 [&_ul]:list-none [&_ul]:border-s [&_ul]:border-border [&_ul]:ps-4">
      {nodes.map((node) => (
        <li key={node.slug}>
          {node.children?.length ? (
            <details open={containsActive(node, activeSlug)}>
              <summary className="cursor-pointer rounded-xs px-1 py-2 text-ink transition-colors duration-fast hover:bg-hover-bg hover:text-accent-text">
                {node.name}
              </summary>
              <TreeNodes nodes={node.children} activeSlug={activeSlug} />
            </details>
          ) : (
            <Link
              to={genreHref(node.slug)}
              aria-current={node.slug === activeSlug ? 'page' : undefined}
              className="block rounded-xs px-2 py-1 text-ink-muted no-underline transition-colors duration-fast hover:bg-hover-bg hover:text-accent-text aria-[current=page]:font-semibold aria-[current=page]:text-accent-text"
            >
              {node.name}
            </Link>
          )}
        </li>
      ))}
    </ul>
  );
}

export function GenreTree({ nodes = [], activeSlug }: GenreTreeProps) {
  const allHref = browseHref(nodes);
  // The row names the term it actually opens. `browseHref` resolves the FIRST
  // ROOT term's listing, so the two are read off the same node rather than
  // from a label that outlives the destination.
  const firstTerm = nodes[0]?.name ?? null;

  return (
    <div>
      {/*
        The panel's own title is the «التصنيفات» entry above it, so no second
        heading is drawn inside. Its place is taken by a destination — the
        standalone browse page — rather than by a label.
      */}
      {/*
        Omitted entirely while the vocabulary has no first term. There is no bare
        `/genres` route on either host — the tree IS the vocabulary's surface —
        so the row's destination is the first root term's own listing.
      */}
      {allHref && firstTerm ? (
        <Link
          to={allHref}
          className="mb-3 block border-b border-border pb-3 text-sm font-semibold text-ink no-underline hover:text-accent-text"
        >
          {chromeCopy.genreTree.all(firstTerm)}
        </Link>
      ) : null}

      {nodes.length === 0 ? null : (
        <nav aria-label={chromeCopy.genreTree.label}>
          <TreeNodes nodes={nodes} activeSlug={activeSlug} />
        </nav>
      )}
    </div>
  );
}
